import {
  Box,
  Button,
  IconButton,
  Paper,
  Slide,
  Typography,
} from "@mui/material";
import {
  IconBrandApple,
  IconDownload,
  IconRefresh,
  IconShare2,
  IconSquarePlus,
  IconX,
} from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { usePwaInstallPrompt } from "../../hooks/usePwaInstallPrompt";
import { track } from "../../services/analytics";
import { isIosSafari, isStandalonePwa } from "../../utils/pwa";
import { promptPwaInstall } from "../../utils/pwaInstallPrompt";

const ANDROID_DISMISS_KEY = "pwa-install-dismissed";
const IOS_DISMISS_KEY = "pwa-ios-install-dismissed";

/**
 * PWA install + update surface for the FindASide web app.
 *
 *  - Android/desktop Chrome: uses the shared beforeinstallprompt capture to
 *    show a custom "Install app" banner that triggers the native prompt.
 *  - iOS Safari: shows an "Add to Home Screen" instruction banner (there is no
 *    programmatic install on iOS).
 *  - Any platform: shows a "new version available" bar when the service worker
 *    has updated, and refreshes into the new version on tap.
 *
 * Renders nothing when already installed.
 */
export default function InstallPrompt() {
  const { deferredPrompt, installed } = usePwaInstallPrompt();
  const [showAndroid, setShowAndroid] = useState(false);
  const [showIos, setShowIos] = useState(false);
  const [updateReg, setUpdateReg] = useState(null);

  useEffect(() => {
    if (isStandalonePwa()) return undefined;

    // iOS never fires beforeinstallprompt, so decide up front.
    if (isIosSafari() && !localStorage.getItem(IOS_DISMISS_KEY)) {
      setShowIos(true);
      track("pwa_install_prompt_shown", { platform: "ios" });
    }

    const onUpdateAvailable = (event) => {
      setUpdateReg(event.detail || null);
    };
    window.addEventListener("pwa:update-available", onUpdateAvailable);
    return () => {
      window.removeEventListener("pwa:update-available", onUpdateAvailable);
    };
  }, []);

  // Surface the Android banner whenever the shared store captures a prompt,
  // whether that happened before or after this component mounted.
  useEffect(() => {
    if (deferredPrompt && !localStorage.getItem(ANDROID_DISMISS_KEY)) {
      setShowAndroid(true);
      track("pwa_install_prompt_shown", { platform: "android" });
    }
  }, [deferredPrompt]);

  useEffect(() => {
    if (installed) {
      setShowAndroid(false);
      setShowIos(false);
      track("pwa_installed");
    }
  }, [installed]);

  const handleInstall = useCallback(async () => {
    try {
      const choice = await promptPwaInstall();
      if (choice) {
        track("pwa_install_prompt_result", {
          platform: "android",
          outcome: choice.outcome || "unknown",
        });
      }
    } finally {
      setShowAndroid(false);
    }
  }, []);

  const dismissAndroid = useCallback(() => {
    localStorage.setItem(ANDROID_DISMISS_KEY, "1");
    setShowAndroid(false);
    track("pwa_install_dismissed", { platform: "android" });
  }, []);

  const dismissIos = useCallback(() => {
    localStorage.setItem(IOS_DISMISS_KEY, "1");
    setShowIos(false);
    track("pwa_install_dismissed", { platform: "ios" });
  }, []);

  const handleRefresh = useCallback(() => {
    track("pwa_update_accepted");
    const waiting = updateReg?.waiting;
    if (waiting) {
      // Activate the new worker, then reload once it takes control.
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        () => window.location.reload(),
        { once: true },
      );
      waiting.postMessage("SKIP_WAITING");
    } else {
      window.location.reload();
    }
    setUpdateReg(null);
  }, [updateReg]);

  const barSx = {
    position: "fixed",
    left: { xs: 8, sm: "auto" },
    right: { xs: 8, sm: 24 },
    bottom: { xs: 8, sm: 24 },
    zIndex: 1400,
    maxWidth: { sm: 420 },
    p: 2,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    borderRadius: 2,
  };

  return (
    <>
      {/* Update-available bar (highest priority) */}
      <Slide direction="up" in={!!updateReg} mountOnEnter unmountOnExit>
        <Paper elevation={6} sx={barSx} role="alert">
          <IconRefresh size={22} color="#1976d2" style={{ flexShrink: 0 }} />
          <Typography variant="body2" sx={{ flex: 1 }}>
            A new version of FindASide is available.
          </Typography>
          <Button size="small" variant="contained" onClick={handleRefresh}>
            Refresh
          </Button>
        </Paper>
      </Slide>

      {/* Android / desktop install banner */}
      <Slide
        direction="up"
        in={showAndroid && !updateReg}
        mountOnEnter
        unmountOnExit
      >
        <Paper elevation={6} sx={barSx} role="dialog" aria-label="Install app">
          <IconDownload size={22} color="#1976d2" style={{ flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2">Install FindASide</Typography>
            <Typography variant="caption" color="text.secondary">
              Add it to your home screen for quick access.
            </Typography>
          </Box>
          <Button size="small" variant="contained" onClick={handleInstall}>
            Install
          </Button>
          <IconButton
            size="small"
            aria-label="Dismiss"
            onClick={dismissAndroid}
          >
            <IconX size={18} />
          </IconButton>
        </Paper>
      </Slide>

      {/* iOS "Add to Home Screen" instructions */}
      <Slide
        direction="up"
        in={showIos && !updateReg}
        mountOnEnter
        unmountOnExit
      >
        <Paper
          elevation={6}
          sx={{ ...barSx, alignItems: "flex-start" }}
          role="dialog"
          aria-label="Install instructions"
        >
          <IconBrandApple size={22} style={{ flexShrink: 0, marginTop: 2 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" gutterBottom>
              Install FindASide
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.5,
              }}
            >
              Tap
              <IconShare2 size={15} style={{ verticalAlign: "middle" }} />
              then
              <b>Add to Home Screen</b>
              <IconSquarePlus size={15} style={{ verticalAlign: "middle" }} />
            </Typography>
          </Box>
          <IconButton size="small" aria-label="Dismiss" onClick={dismissIos}>
            <IconX size={18} />
          </IconButton>
        </Paper>
      </Slide>
    </>
  );
}
